/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxTooltip from 'devextreme/ui/tooltip';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxoAnimationModule } from './nested/animation';
import { DxoHideModule } from './nested/hide';
import { DxoShowModule } from './nested/show';
import { DxoHideEventModule } from './nested/hide-event';
import { DxoPositionModule } from './nested/position';
import { DxoAtModule } from './nested/at';
import { DxoBoundaryOffsetModule } from './nested/boundary-offset';
import { DxoCollisionModule } from './nested/collision';
import { DxoMyModule } from './nested/my';
import { DxoOffsetModule } from './nested/offset';
import { DxoShowEventModule } from './nested/show-event';





/**
 * The Tooltip widget displays a tooltip for a specified element on the page.
 */

export class DxTooltipComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxTooltip;


    /**
     * Configures widget visibility animations. This object contains two fields: show and hide.
     */
    
    get animation(): any {
        return this._getOption('animation');
    }
    set animation(value: any) {
        this._setOption('animation', value);
    }


    /**
     * Use the toolbarItems option instead.
     */
    
    get buttons(): Array<any> {
        return this._getOption('buttons');
    }
    set buttons(value: Array<any>) {
        this._setOption('buttons', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is closed if a user presses the Back hardware button.
     */
    
    get closeOnBackButton(): boolean {
        return this._getOption('closeOnBackButton');
    }
    set closeOnBackButton(value: boolean) {
        this._setOption('closeOnBackButton', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is closed if a user clicks outside of the popover window and outside the target element.
     */
    
    get closeOnOutsideClick(): boolean|
        Function {
        return this._getOption('closeOnOutsideClick');
    }
    set closeOnOutsideClick(value: boolean|
        Function) {
        this._setOption('closeOnOutsideClick', value);
    }


    /**
     * A template to be used for rendering widget content.
     */
    
    get contentTemplate(): any {
        return this._getOption('contentTemplate');
    }
    set contentTemplate(value: any) {
        this._setOption('contentTemplate', value);
    }


    /**
     * Specifies whether widget content is rendered when the widget is shown or when rendering the widget.
     */
    
    get deferRendering(): boolean {
        return this._getOption('deferRendering');
    }
    set deferRendering(value: boolean) {
        this._setOption('deferRendering', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies options of popover hiding.
     */
    
    get hideEvent(): string|
        { delay?: number, name?: string } {
        return this._getOption('hideEvent');
    }
    set hideEvent(value: string|
        { delay?: number, name?: string }) {
        this._setOption('hideEvent', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Specifies the maximum height the widget can reach while resizing.
     */
    
    get maxHeight(): number|
        Function|
        string {
        return this._getOption('maxHeight');
    }
    set maxHeight(value: number|
        Function|
        string) {
        this._setOption('maxHeight', value);
    }


    /**
     * Specifies the maximum width the widget can reach while resizing.
     */
    
    get maxWidth(): number|
        Function|
        string {
        return this._getOption('maxWidth');
    }
    set maxWidth(value: number|
        Function|
        string) {
        this._setOption('maxWidth', value);
    }


    /**
     * Specifies the minimum height the widget can reach while resizing.
     */
    
    get minHeight(): number|
        Function|
        string {
        return this._getOption('minHeight');
    }
    set minHeight(value: number|
        Function|
        string) {
        this._setOption('minHeight', value);
    }


    /**
     * Specifies the minimum width the widget can reach while resizing.
     */
    
    get minWidth(): number|
        Function|
        string {
        return this._getOption('minWidth');
    }
    set minWidth(value: number|
        Function|
        string) {
        this._setOption('minWidth', value);
    }


    /**
     * An object defining widget positioning options.
     */
    
    get position(): DevExpress.positionConfig|
        string {
        return this._getOption('position');
    }
    set position(value: DevExpress.positionConfig|
        string) {
        this._setOption('position', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * A Boolean value specifying whether or not the main screen is inactive while the widget is active.
     */
    
    get shading(): boolean {
        return this._getOption('shading');
    }
    set shading(value: boolean) {
        this._setOption('shading', value);
    }


    /**
     * Specifies the shading color.
     */
    
    get shadingColor(): string {
        return this._getOption('shadingColor');
    }
    set shadingColor(value: string) {
        this._setOption('shadingColor', value);
    }


    /**
     * Specifies options for displaying the widget.
     */
    
    get showEvent(): string|
        { delay?: number, name?: string } {
        return this._getOption('showEvent');
    }
    set showEvent(value: string|
        { delay?: number, name?: string }) {
        this._setOption('showEvent', value);
    }


    /**
     * The target element associated with a popover.
     */
    
    get target(): Element|
        JQuery {
        return this._getOption('target');
    }
    set target(value: Element|
        JQuery) {
        this._setOption('target', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the hidden event.
     */
     onHidden: EventEmitter<any>;

    /**
     * A handler for the hiding event.
     */
     onHiding: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the showing event.
     */
     onShowing: EventEmitter<any>;

    /**
     * A handler for the shown event.
     */
     onShown: EventEmitter<any>;

    /**
     * A handler for the animationChange event.
     */
     animationChange: EventEmitter<any>;

    /**
     * A handler for the buttonsChange event.
     */
     buttonsChange: EventEmitter<Array<any>>;

    /**
     * A handler for the closeOnBackButtonChange event.
     */
     closeOnBackButtonChange: EventEmitter<boolean>;

    /**
     * A handler for the closeOnOutsideClickChange event.
     */
     closeOnOutsideClickChange: EventEmitter<boolean|
        Function>;

    /**
     * A handler for the contentTemplateChange event.
     */
     contentTemplateChange: EventEmitter<any>;

    /**
     * A handler for the deferRenderingChange event.
     */
     deferRenderingChange: EventEmitter<boolean>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hideEventChange event.
     */
     hideEventChange: EventEmitter<string|
        { delay?: number, name?: string }>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the maxHeightChange event.
     */
     maxHeightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the maxWidthChange event.
     */
     maxWidthChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the minHeightChange event.
     */
     minHeightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the minWidthChange event.
     */
     minWidthChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the positionChange event.
     */
     positionChange: EventEmitter<DevExpress.positionConfig|
        string>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the shadingChange event.
     */
     shadingChange: EventEmitter<boolean>;

    /**
     * A handler for the shadingColorChange event.
     */
     shadingColorChange: EventEmitter<string>;

    /**
     * A handler for the showEventChange event.
     */
     showEventChange: EventEmitter<string|
        { delay?: number, name?: string }>;

    /**
     * A handler for the targetChange event.
     */
     targetChange: EventEmitter<Element|
        JQuery>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;





    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'hidden', emit: 'onHidden' },
            { subscribe: 'hiding', emit: 'onHiding' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'showing', emit: 'onShowing' },
            { subscribe: 'shown', emit: 'onShown' },
            { emit: 'animationChange' },
            { emit: 'buttonsChange' },
            { emit: 'closeOnBackButtonChange' },
            { emit: 'closeOnOutsideClickChange' },
            { emit: 'contentTemplateChange' },
            { emit: 'deferRenderingChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'heightChange' },
            { emit: 'hideEventChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'maxHeightChange' },
            { emit: 'maxWidthChange' },
            { emit: 'minHeightChange' },
            { emit: 'minWidthChange' },
            { emit: 'positionChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'shadingChange' },
            { emit: 'shadingColorChange' },
            { emit: 'showEventChange' },
            { emit: 'targetChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxTooltip(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('buttons', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('buttons');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-tooltip',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'animation': [{ type: Input },],
'buttons': [{ type: Input },],
'closeOnBackButton': [{ type: Input },],
'closeOnOutsideClick': [{ type: Input },],
'contentTemplate': [{ type: Input },],
'deferRendering': [{ type: Input },],
'disabled': [{ type: Input },],
'elementAttr': [{ type: Input },],
'height': [{ type: Input },],
'hideEvent': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'maxHeight': [{ type: Input },],
'maxWidth': [{ type: Input },],
'minHeight': [{ type: Input },],
'minWidth': [{ type: Input },],
'position': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'shading': [{ type: Input },],
'shadingColor': [{ type: Input },],
'showEvent': [{ type: Input },],
'target': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onContentReady': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onHidden': [{ type: Output },],
'onHiding': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onShowing': [{ type: Output },],
'onShown': [{ type: Output },],
'animationChange': [{ type: Output },],
'buttonsChange': [{ type: Output },],
'closeOnBackButtonChange': [{ type: Output },],
'closeOnOutsideClickChange': [{ type: Output },],
'contentTemplateChange': [{ type: Output },],
'deferRenderingChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hideEventChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'maxHeightChange': [{ type: Output },],
'maxWidthChange': [{ type: Output },],
'minHeightChange': [{ type: Output },],
'minWidthChange': [{ type: Output },],
'positionChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'shadingChange': [{ type: Output },],
'shadingColorChange': [{ type: Output },],
'showEventChange': [{ type: Output },],
'targetChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
};
}


export class DxTooltipModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoHideEventModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxoShowEventModule,
    DxTemplateModule
  ],
  declarations: [
    DxTooltipComponent
  ],
  exports: [
    DxTooltipComponent,
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoHideEventModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxoShowEventModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
